import { useRef } from "react";
import DatePicker from "react-datepicker";
import { Controller } from "react-hook-form";
import { format, isValid } from "date-fns";
import { Box, Flex, Text, Tooltip, Wrap, WrapItem, IconButton } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import "react-datepicker/dist/react-datepicker.css";

import { IField } from "../../interfaces/IField";
import { DefinedValidations } from "../../interfaces/Validations";
import { Asterisk, CalendarIcon } from "../../icons";
import useValidate from "../../hooks/useValidate";

interface IMultiDatepicker extends IField {
  placeholder?: string;
  styles?: { font?: string; fullWidth?: boolean };
  canSelectFutureDate?: boolean;
}

const toStoredArray = (v: unknown): string[] => {
  if (v == null || v === "") return [];
  if (Array.isArray(v)) return v.filter(Boolean).map(String);
  return [String(v)];
};

const parseStored = (s: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isValid(d) ? d : null;
};

const definedValidations: DefinedValidations = {
  notEmpty: (label, validationValue, value) => {
    if (validationValue && toStoredArray(value).length === 0) {
      return `Cannot be empty`;
    }
  },
};

const MultiDatepicker = ({
  control,
  name,
  label = "",
  placeholder = "Add a date",
  tooltip = "",
  required,
  validations = {},
  disabled = false,
  styles,
  canSelectFutureDate,
  variant,
}: IMultiDatepicker) => {
  const addPickerRef = useRef<any>(null);
  const validate = useValidate(label || name, validations || {}, definedValidations);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate }}
      render={({ field, fieldState }) => {
        const { onChange, onBlur, value } = field;
        const { error } = fieldState;
        const dates = toStoredArray(value).map(parseStored).filter((d): d is Date => d != null);

        const addDate = (d: Date | null) => {
          if (!d || disabled) return;
          const nextStr = format(d, "M/d/yyyy");
          const current = toStoredArray(value);
          const exists = current.some((s) => parseStored(s)?.getTime() === d.getTime());
          if (exists) return;
          onChange([...current, nextStr]);
        };

        const removeAt = (idx: number) => {
          const d = dates[idx];
          if (!d) return;
          const t = d.getTime();
          const current = toStoredArray(value);
          onChange(current.filter((s) => parseStored(s)?.getTime() !== t));
        };

        return (
          <Box w="full" id={name} minH="80px">
            {label ? (
              <Flex pt={2} align="center" justify="space-between" mb="none">
                <Box
                  color={error ? "datepicker.labelFont.error" : styles?.font || "datepicker.labelFont.normal"}
                  fontWeight="bold"
                  fontSize="ssm"
                  mb="4.5px"
                  minH="16px"
                >
                  {label}
                  {!disabled && validations?.notEmpty && (
                    <Asterisk h="8px" ml="5px" mb="8px" fill="datepicker.iconAsterisk" stroke="datepicker.iconAsterisk" />
                  )}
                  {tooltip && (
                    <Tooltip hasArrow label={tooltip} placement="top">
                      <InfoOutlineIcon mb={1} h="14px" />
                    </Tooltip>
                  )}
                </Box>
              </Flex>
            ) : (
              <Box pt="23px" />
            )}

            <Wrap spacing={2} mb={2}>
              {dates.map((d, idx) => (
                <WrapItem key={`${d.getTime()}-${idx}`}>
                  <Flex
                    align="center"
                    borderWidth="1px"
                    borderRadius="md"
                    px={2}
                    py={1}
                    fontSize="smm"
                    borderColor={error ? "datepicker.border.error" : "datepicker.border.normal"}
                    bg={disabled ? "datepicker.disabled.bg" : "datepicker.bg"}
                  >
                    <Text>{format(d, "dd MMM yyyy")}</Text>
                    {!disabled && (
                      <IconButton
                        aria-label="Remove date"
                        icon={<Text lineHeight={1}>×</Text>}
                        size="xs"
                        variant="ghost"
                        ml={1}
                        onClick={() => removeAt(idx)}
                      />
                    )}
                  </Flex>
                </WrapItem>
              ))}
            </Wrap>

            {!disabled && (
              <Flex
                maxW={styles?.fullWidth ? "full" : "360px"}
                minW="130px"
                w="full"
                pl="3px"
                align="center"
                borderRadius="8px"
                borderWidth="1px"
                h="42px"
                mt="2px"
                color="datepicker.font"
                bg="#FFF"
                borderColor={error ? "datepicker.border.error" : "datepicker.border.normal"}
                cursor="pointer"
                justify="space-between"
                onTouchEnd={() => {
                  addPickerRef.current?.setOpen(true);
                }}
              >
                <DatePicker
                  css={{ paddingTop: "0" }}
                  autoComplete="off"
                  dateFormat={variant === "date" ? "dd MMM yyyy" : "dd MMM yyyy"}
                  onChange={(d: Date | null) => addDate(d)}
                  onCalendarClose={onBlur}
                  selected={null}
                  placeholderText={placeholder}
                  ref={addPickerRef}
                  disabledKeyboardNavigation
                  showYearDropdown
                  dropdownMode="select"
                  dateFormatCalendar="MMMM"
                  maxDate={canSelectFutureDate ? undefined : new Date()}
                  calendarStartDay={1}
                  popperProps={{ placement: "top" }}
                />
                <CalendarIcon
                  w="15px"
                  h="16px"
                  mr="15px"
                  color="datepicker.iconCalender"
                onClick={() => {
                  addPickerRef.current?.setOpen(true);
                }}
                />
              </Flex>
            )}
            {disabled && dates.length === 0 && (
              <Text pl="17px" fontSize="smm" color="datepicker.disabled.font">
                —
              </Text>
            )}
            {error && (
              <Box fontSize="ssm" ml={1} color="datepicker.error">
                {error.message}
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
};

export default MultiDatepicker;
